import classNames from 'classnames'
import {
  Children,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useIntersectionObserver } from '@hooks/intersection-observer'
import { useBreakpoint } from '@hooks/breakpoints'
import { breakpoints } from '@utils/breakpoints'

export function Carousel({
  children,
  className,
  itemClassName,
}: {
  children: ReactNode
  className?: string
  itemClassName?: string
}) {
  const [carousel, setCarousel] = useState<HTMLDivElement | null>(null)
  const [prevArrowVisible, setPrevArrowVisible] = useState(false)
  const [nextArrowvisible, setNextArrowVisible] = useState(false)
  const isMobile = useBreakpoint(breakpoints.sm, { max: true })

  const scrollActionWidth = useRef<number>(0)

  const handlePrev = useCallback(() => {
    carousel?.scrollBy({
      left: -1 * (scrollActionWidth.current - (isMobile ? 0 : 148)),
    })
  }, [isMobile, carousel])

  const handleNext = useCallback(() => {
    carousel?.scrollBy({
      left: scrollActionWidth.current - (isMobile ? 0 : 148),
    })
  }, [isMobile, carousel])

  const itemDetector = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry, index) => {
        const target = entry.target
        const childCount = carousel?.childElementCount ?? 0

        if (entry.isIntersecting) {
          target?.classList.add('opacity-100')
          target?.classList.remove('opacity-25')
        } else {
          target?.classList.add('opacity-25')
          target?.classList.remove('opacity-100')
        }
        if (index === 0) {
          if (entry.isIntersecting && prevArrowVisible)
            setPrevArrowVisible(false)
          else if (!entry.isIntersecting && !prevArrowVisible)
            setPrevArrowVisible(true)
        } else if (index === childCount - 1) {
          if (entry.isIntersecting && nextArrowvisible)
            setNextArrowVisible(false)
          else if (!entry.isIntersecting && !nextArrowvisible)
            setNextArrowVisible(true)
        }
      })
    },
    [carousel?.childElementCount, nextArrowvisible, prevArrowVisible]
  )

  const itemObserver = useIntersectionObserver(itemDetector, {
    root: carousel,
    rootMargin: '16px 0px',
    threshold: 0.9,
  })

  useEffect(() => {
    // if (carousel) scrollActionWidth.current = carousel?.clientWidth
    if (carousel && carousel.childElementCount > 0) {
      for (const el of Array.from(carousel.children)) {
        itemObserver?.observe(el)
      }
      scrollActionWidth.current =
        (carousel?.clientWidth ?? 0) -
          (isMobile ? 0 : (carousel?.children[0]?.clientWidth ?? 0) / 2) || 100
    }

    return () => {
      itemObserver?.disconnect()
    }
  }, [itemObserver, carousel, isMobile])

  return (
    <div className='relative flex items-center justify-center w-full h-full'>
      <div
        className={classNames(
          'w-5 xxxs:w-8 h-full flex items-center justify-center transition-opacity',
          {
            'opacity-100': prevArrowVisible,
            '!opacity-0': !prevArrowVisible,
          }
        )}
      >
        {prevArrowVisible && (
          <button
            disabled={!prevArrowVisible}
            onClick={handlePrev}
            className='p-1 !bg-transparent !border-none'
          >
            <ChevronLeftIcon className='w-4 h-4 xxxs:w-8 xxxs:h-8 stroke-foreground dark:stroke-dark-foreground' />
          </button>
        )}
      </div>
      <div
        ref={setCarousel}
        style={{ scrollbarWidth: 'none' }}
        className={classNames(
          className,
          '@container flex items-center justify-start w-[calc(100%-40px)] xxxs:w-[calc(100%-64px)] h-full overflow-x-auto overflow-y-hidden p-2',
          'snap-mandatory sm:snap-proximity snap-x scroll-smooth space-x-8 scroll-mx-8'
        )}
      >
        {Children.map(children, (child, index) => (
          <div
            key={index}
            className={classNames(
              itemClassName,
              'flex items-center justify-center flex-1 transition-all duration-300 ease-in-out snap-center snap-normal'
            )}
          >
            {child}
          </div>
        ))}
      </div>
      <div
        className={classNames(
          'w-5 xxxs:w-8 h-full flex items-center justify-center transition-opacity',
          {
            'opacity-100': nextArrowvisible,
            '!opacity-0': !nextArrowvisible,
          }
        )}
      >
        {nextArrowvisible && (
          <button
            disabled={!nextArrowvisible}
            onClick={handleNext}
            className='p-1 !bg-transparent !border-none'
          >
            <ChevronRightIcon className='w-4 h-4 xxxs:w-8 xxxs:h-8 stroke-foreground dark:stroke-dark-foreground' />
          </button>
        )}
      </div>
    </div>
  )
}
